import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Plus,
  Tag,
  Calendar,
  Users,
  AlertCircle,
  Eye,
  Copy,
  Check,
  X,
  Search,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { 
  partnerApi, 
  PromoCode, 
  CreatePromoCodeDto, 
  PartnerPromoCodeStats,
  PromoCodeActivation 
} from '../api/client';

const PromoCodes: React.FC = () => {
  const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);
  const [stats, setStats] = useState<PartnerPromoCodeStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [selectedPromoCode, setSelectedPromoCode] = useState<PromoCode | null>(null);
  const [activations, setActivations] = useState<PromoCodeActivation[]>([]);
  const [activationsLoading, setActivationsLoading] = useState(false);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  
  // Состояние для пагинации промокодов
  const [promoCodesPage, setPromoCodesPage] = useState(1);
  const [promoCodesLimit] = useState(10);
  const [promoCodesTotal, setPromoCodesTotal] = useState(0);
  const [promoCodesTotalPages, setPromoCodesTotalPages] = useState(0);
  const [promoCodesSearch, setPromoCodesSearch] = useState('');
  
  // Состояние для пагинации активаций
  const [activationsPage, setActivationsPage] = useState(1);
  const [activationsLimit] = useState(20);
  const [activationsTotal, setActivationsTotal] = useState(0);
  const [activationsTotalPages, setActivationsTotalPages] = useState(0);
  
  // Состояние для фильтрации и поиска активаций
  const [activationsSearch, setActivationsSearch] = useState('');
  const [activationsStatus, setActivationsStatus] = useState<string>('');
  
  const [createForm, setCreateForm] = useState<CreatePromoCodeDto>({
    code: '',
    bonusPercent: 25,
    type: 'DEPOSIT'
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async (page = promoCodesPage, search = promoCodesSearch) => {
    try {
      setLoading(true);
      const [promoCodesResponse, statsResponse] = await Promise.all([
        partnerApi.getPartnerPromoCodes(page, promoCodesLimit, search),
        partnerApi.getPartnerPromoCodeStats()
      ]);
      setPromoCodes(promoCodesResponse.data);
      setPromoCodesTotal(promoCodesResponse.total);
      setPromoCodesTotalPages(promoCodesResponse.totalPages);
      setStats(statsResponse);
    } catch (error) {
      // Failed to load promo codes
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePromoCode = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!createForm.code.trim()) {
      alert('Введите код промокода');
      return;
    }

    try {
      setCreating(true);
      // Убеждаемся, что тип всегда DEPOSIT для партнеров и процент бонуса фиксированный 25%
      const formData = { ...createForm, type: 'DEPOSIT' as const, bonusPercent: 25 };
      await partnerApi.createPartnerPromoCode(formData);
      await loadData(promoCodesPage, promoCodesSearch);
      setCreateForm({ code: '', bonusPercent: 25, type: 'DEPOSIT' });
      setShowCreateForm(false);
    } catch (error: any) {
      // Failed to create promo code
      
      let errorMessage = 'Ошибка при создании промокода';
      
      // Проверяем разные возможные форматы ошибки
      const errorData = error.response?.data;
      const errorText = errorData ? JSON.stringify(errorData).toLowerCase() : '';
      
      if (errorData) {
        // Проверяем message
        if (errorData.message && typeof errorData.message === 'string') {
          const message = errorData.message.toLowerCase();
          if (message.includes('promocodecodeAlreadyexists') || 
              message.includes('уже существует') ||
              message.includes('already exists') ||
              message.includes('duplicate') ||
              message.includes('дубликат')) {
            errorMessage = 'Промокод с таким кодом уже существует. Выберите другой код.';
          } else if (message.includes('лимит') || message.includes('limit')) {
            // Используем сообщение из API, если оно есть
            errorMessage = errorData.message || 'Лимит на создание промокодов исчерпан';
          } else {
            errorMessage = errorData.message;
          }
        }
        // Проверяем error
        else if (errorData.error && typeof errorData.error === 'string') {
          const errorStr = errorData.error.toLowerCase();
          if (errorStr.includes('promocodecodeAlreadyexists') || 
              errorStr.includes('уже существует') ||
              errorStr.includes('already exists') ||
              errorStr.includes('duplicate') ||
              errorStr.includes('дубликат')) {
            errorMessage = 'Промокод с таким кодом уже существует. Выберите другой код.';
          } else {
            errorMessage = errorData.error;
          }
        }
        // Проверяем весь JSON на наличие ключевых слов
        else if (errorText && (errorText.includes('promocodecodeAlreadyexists') || 
                 errorText.includes('уже существует') ||
                 errorText.includes('already exists') ||
                 errorText.includes('duplicate') ||
                 errorText.includes('дубликат'))) {
          errorMessage = 'Промокод с таким кодом уже существует. Выберите другой код.';
        }
      }
      
      alert(errorMessage);
    } finally {
      setCreating(false);
    }
  };

  const loadActivations = async (promoCode: PromoCode, page: number = 1, search?: string, status?: string) => {
    try {
      setActivationsLoading(true);
      setSelectedPromoCode(promoCode);
      const response = await partnerApi.getPartnerPromoCodeActivations(
        promoCode._id, 
        page, 
        activationsLimit, 
        search, 
        status
      );
      setActivations(response.data);
      setActivationsTotal(response.total);
      setActivationsTotalPages(response.totalPages);
      setActivationsPage(page);
    } catch (error) {
      // Failed to load activations
      alert('Ошибка при загрузке активаций');
    } finally {
      setActivationsLoading(false);
    }
  };

  const handleActivationsSearch = () => {
    if (selectedPromoCode) {
      setActivationsPage(1);
      loadActivations(selectedPromoCode, 1, activationsSearch, activationsStatus);
    }
  };

  const handleActivationsStatusChange = (status: string) => {
    setActivationsStatus(status);
    if (selectedPromoCode) {
      setActivationsPage(1);
      loadActivations(selectedPromoCode, 1, activationsSearch, status);
    }
  };

  const handleActivationsPageChange = (page: number) => {
    if (selectedPromoCode) {
      loadActivations(selectedPromoCode, page, activationsSearch, activationsStatus);
    }
  };

  const resetActivationsFilters = () => {
    setActivationsSearch('');
    setActivationsStatus('');
    setActivationsPage(1);
    if (selectedPromoCode) {
      loadActivations(selectedPromoCode, 1);
    }
  };

  const copyPromoCode = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ru-RU');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'bonusPaid': return 'text-green-400';
      case 'apply': return 'text-yellow-400';
      case 'expired': return 'text-red-400';
      default: return 'text-tesla-gray';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'bonusPaid': return 'Выплачен';
      case 'apply': return 'Активирован';
      case 'expired': return 'Истек';
      default: return status;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-tesla-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-tesla-black text-white pt-24 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-4xl font-bold mb-2">Управление промокодами</h1>
              <p className="text-tesla-gray">Создавайте и управляйте промокодами для привлечения пользователей</p>
            </div>
            <button
              onClick={() => setShowCreateForm(true)}
              disabled={!stats?.canCreateMore}
              className={`tesla-button flex items-center space-x-2 whitespace-nowrap ${
                !stats?.canCreateMore ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <Plus className="w-5 h-5" />
              <span>Создать промокод</span>
            </button>
          </div>
          {!stats?.canCreateMore && (
            <p className="text-yellow-400 text-sm mt-2">
              Достигнут лимит на создание промокодов
            </p>
          )}
        </motion.div>

        {/* Статистика */}
        {stats && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8"
          >
            <div className="tesla-glass p-6 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-tesla-gray text-xs uppercase tracking-wider">Всего промокодов</p>
                  <p className="text-2xl font-bold">{stats.totalPromoCodes}</p>
                </div>
                <Tag className="w-8 h-8 text-white" />
              </div>
            </div>

            <div className="tesla-glass p-6 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-tesla-gray text-xs uppercase tracking-wider">Создано за неделю</p>
                  <p className="text-2xl font-bold">{stats.weeklyCreated}</p>
                </div>
                <Calendar className="w-8 h-8 text-blue-400" />
              </div>
            </div>

            <div className="tesla-glass p-6 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-tesla-gray text-xs uppercase tracking-wider">Лимит в неделю</p>
                  <p className="text-2xl font-bold">{stats.weeklyLimit}</p>
                </div>
                <AlertCircle className="w-8 h-8 text-yellow-400" />
              </div>
            </div>

            <div className="tesla-glass p-6 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-tesla-gray text-xs uppercase tracking-wider">Можно создать</p>
                  <p className="text-2xl font-bold">
                    {stats.canCreateMore ? stats.weeklyLimit - stats.weeklyCreated : 0}
                  </p>
                </div>
                <Plus className={`w-8 h-8 ${stats.canCreateMore ? 'text-green-400' : 'text-red-400'}`} />
              </div>
            </div>
          </motion.div>
        )}



        {/* Список промокодов */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="tesla-card overflow-hidden"
        >
          <div className="p-6 border-b border-white/10">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <h2 className="text-xl font-semibold">Ваши промокоды</h2>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-tesla-gray" />
                <input
                  type="text"
                  placeholder="Поиск промокодов..."
                  value={promoCodesSearch}
                  onChange={(e) => {
                    setPromoCodesSearch(e.target.value);
                    setPromoCodesPage(1);
                    loadData(1, e.target.value);
                  }}
                  className="tesla-input pl-10 w-64"
                />
              </div>
            </div>
          </div>

          {promoCodes.length === 0 ? (
            <div className="p-8 text-center">
              <Tag className="w-16 h-16 text-tesla-gray mx-auto mb-4" />
              <p className="text-tesla-gray">У вас пока нет промокодов</p>
              <p className="text-tesla-gray text-sm opacity-70">Создайте первый промокод для привлечения пользователей</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-black/20">
                  <tr>
                    <th className="text-left py-4 px-6 text-tesla-gray font-medium text-xs uppercase tracking-wider">Код</th>
                    <th className="text-left py-4 px-6 text-tesla-gray font-medium text-xs uppercase tracking-wider">Бонус</th>
                    <th className="text-left py-4 px-6 text-tesla-gray font-medium text-xs uppercase tracking-wider">Тип</th>
                    <th className="text-left py-4 px-6 text-tesla-gray font-medium text-xs uppercase tracking-wider">Создан</th>
                    <th className="text-left py-4 px-6 text-tesla-gray font-medium text-xs uppercase tracking-wider">Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {promoCodes.map((promoCode) => (
                    <tr key={promoCode._id} className="border-t border-white/10 hover:bg-white/5">
                      <td className="py-4 px-6">
                        <div className="flex items-center space-x-3">
                          <code className="bg-black/40 px-3 py-1 rounded-lg font-mono text-white">
                            {promoCode.code}
                          </code>
                          <button
                            onClick={() => copyPromoCode(promoCode.code)}
                            className="text-tesla-gray hover:text-white transition-colors"
                          >
                            {copiedCode === promoCode.code ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                          </button>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <span className="text-white font-semibold">+{promoCode.bonusPercent}%</span>
                      </td>
                      <td className="py-4 px-6">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          promoCode.type === 'DEPOSIT' 
                            ? 'bg-blue-500/20 text-blue-400' 
                            : 'bg-purple-500/20 text-purple-400'
                        }`}>
                          {promoCode.type === 'DEPOSIT' ? 'На депозит' : 'Бесплатный кейс'}
                        </span>
                      </td>
                      <td className="py-4 px-6 text-tesla-gray">
                        {formatDate(promoCode.createdAt)}
                      </td>
                      <td className="py-4 px-6">
                        <button
                          onClick={() => {
                            setActivationsPage(1);
                            setActivationsSearch('');
                            setActivationsStatus('');
                            loadActivations(promoCode, 1);
                          }}
                          className="inline-flex items-center text-tesla-gray hover:text-white transition-colors"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Активации
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          
          {/* Пагинация промокодов */}
          {promoCodesTotalPages > 1 && (
            <div className="p-6 border-t border-white/10">
              <div className="flex items-center justify-between">
                <div className="text-sm text-tesla-gray">
                  Показано {((promoCodesPage - 1) * promoCodesLimit) + 1}-{Math.min(promoCodesPage * promoCodesLimit, promoCodesTotal)} из {promoCodesTotal}
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => {
                      const newPage = promoCodesPage - 1;
                      setPromoCodesPage(newPage);
                      loadData(newPage, promoCodesSearch);
                    }}
                    disabled={promoCodesPage === 1}
                    className="p-2 rounded-lg tesla-glass disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/10 transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  
                  <div className="flex items-center space-x-1">
                    {Array.from({ length: Math.min(5, promoCodesTotalPages) }, (_, i) => {
                      let pageNum: number;
                      if (promoCodesTotalPages <= 5) {
                        pageNum = i + 1;
                      } else if (promoCodesPage <= 3) {
                        pageNum = i + 1;
                      } else if (promoCodesPage >= promoCodesTotalPages - 2) {
                        pageNum = promoCodesTotalPages - 4 + i;
                      } else {
                        pageNum = promoCodesPage - 2 + i;
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => {
                            setPromoCodesPage(pageNum);
                            loadData(pageNum, promoCodesSearch);
                          }}
                          className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                            pageNum === promoCodesPage
                              ? 'bg-white text-black font-medium'
                              : 'tesla-glass hover:bg-white/10'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  
                  <button
                    onClick={() => {
                      const newPage = promoCodesPage + 1;
                      setPromoCodesPage(newPage);
                      loadData(newPage, promoCodesSearch);
                    }}
                    disabled={promoCodesPage === promoCodesTotalPages}
                    className="p-2 rounded-lg tesla-glass disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/10 transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </motion.div>

        {/* Модальное окно создания промокода */}
        {showCreateForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="tesla-card p-8 max-w-md w-full mx-4"
            >
              <h3 className="text-2xl font-bold mb-6">Создать промокод</h3>
              
              <form onSubmit={handleCreatePromoCode} className="space-y-6">
                <div>
                  <label className="tesla-label">
                    Код промокода
                  </label>
                  <input
                    type="text"
                    value={createForm.code}
                    onChange={(e) => setCreateForm({ ...createForm, code: e.target.value.toUpperCase() })}
                    className="tesla-input w-full"
                    placeholder="MYPROMO"
                    maxLength={20}
                    pattern="^[A-Z0-9]+$"
                    required
                  />
                  <p className="text-tesla-gray text-xs mt-1">Только заглавные буквы и цифры</p>
                </div>

                <div>
                  <label className="tesla-label">
                    Процент бонуса
                  </label>
                  <div className="tesla-input w-full bg-black/20 text-tesla-gray flex items-center justify-center py-3">
                    25% (фиксированный)
                  </div>
                  <p className="text-tesla-gray text-xs mt-1">Процент бонуса фиксирован и составляет 25%</p>
                </div>

                <div className="flex space-x-4">
                  <button
                    type="button"
                    onClick={() => setShowCreateForm(false)}
                    className="flex-1 tesla-button-secondary"
                  >
                    Отмена
                  </button>
                  <button
                    type="submit"
                    disabled={creating}
                    className={`flex-1 tesla-button ${creating ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {creating ? 'Создание...' : 'Создать'}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}

        {/* Модальное окно активаций */}
        {selectedPromoCode && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="tesla-card p-8 max-w-4xl w-full mx-4 max-h-[80vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold">
                  Активации промокода {selectedPromoCode.code}
                </h3>
                <button
                  onClick={() => setSelectedPromoCode(null)}
                  className="text-tesla-gray hover:text-white"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Панель фильтров */}
              <div className="mb-6 p-4 bg-black/20 rounded-lg">
                <div className="flex flex-col sm:flex-row gap-4 items-end">
                  <div className="flex-1">
                    <label className="block text-tesla-gray text-sm mb-2">Поиск по пользователю</label>
                    <div className="relative">
                      <input
                        type="text"
                        value={activationsSearch}
                        onChange={(e) => setActivationsSearch(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleActivationsSearch()}
                        placeholder="Steam ID или никнейм..."
                        className="tesla-input w-full pl-10"
                      />
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-tesla-gray" />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-tesla-gray text-sm mb-2">Статус</label>
                    <select
                      value={activationsStatus}
                      onChange={(e) => handleActivationsStatusChange(e.target.value)}
                      className="tesla-input"
                    >
                      <option value="">Все статусы</option>
                      <option value="apply">Активирован</option>
                      <option value="bonusPaid">Выплачен</option>
                      <option value="expired">Истек</option>
                    </select>
                  </div>
                  
                  <div className="flex gap-2">
                    <button
                      onClick={handleActivationsSearch}
                      className="tesla-button px-4 py-2"
                    >
                      Поиск
                    </button>
                    <button
                      onClick={resetActivationsFilters}
                      className="tesla-button-secondary px-4 py-2"
                    >
                      Сброс
                    </button>
                  </div>
                </div>
              </div>

              {activationsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                </div>
              ) : activations.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="w-16 h-16 text-tesla-gray mx-auto mb-4" />
                  <p className="text-tesla-gray">Промокод еще никто не активировал</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-black/20">
                        <tr>
                          <th className="text-left py-3 px-4 text-tesla-gray font-medium text-xs uppercase tracking-wider">Пользователь</th>
                          <th className="text-left py-3 px-4 text-tesla-gray font-medium text-xs uppercase tracking-wider">Бонус</th>
                          <th className="text-left py-3 px-4 text-tesla-gray font-medium text-xs uppercase tracking-wider">Статус</th>
                          <th className="text-left py-3 px-4 text-tesla-gray font-medium text-xs uppercase tracking-wider">Дата</th>
                        </tr>
                      </thead>
                      <tbody>
                        {activations.map((activation) => (
                          <tr key={activation._id} className="border-t border-white/10">
                            <td className="py-3 px-4">
                              <div className="flex items-center space-x-3">
                                <img 
                                  src={activation.user.avatar} 
                                  alt={activation.user.nickname}
                                  className="w-8 h-8 rounded-full"
                                />
                                <div>
                                  <p className="font-medium text-white">{activation.user.nickname}</p>
                                  <p className="text-tesla-gray text-xs">{activation.user.steamId}</p>
                                </div>
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              <span className="text-white font-semibold">
                                {activation.bonus > 0 ? `${activation.bonus} ₽` : '—'}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                activation.status === 'bonusPaid' 
                                  ? 'bg-green-500/20 text-green-400'
                                  : activation.status === 'apply'
                                  ? 'bg-yellow-500/20 text-yellow-400'
                                  : 'bg-red-500/20 text-red-400'
                              }`}>
                                {getStatusText(activation.status)}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-tesla-gray">
                              {formatDate(activation.createdAt)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="mt-4 text-center text-tesla-gray text-sm">
                    Показано {activations.length} из {activationsTotal} записей
                  </div>

                  {activationsTotalPages > 1 && (
                    <div className="mt-6 flex items-center justify-center space-x-2">
                      <button
                        onClick={() => handleActivationsPageChange(activationsPage - 1)}
                        disabled={activationsPage <= 1}
                        className={`p-2 rounded-lg transition-colors ${
                          activationsPage <= 1
                            ? 'text-tesla-gray cursor-not-allowed'
                            : 'text-white hover:bg-white/10'
                        }`}
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </button>
                      
                      <div className="flex items-center space-x-1">
                        {Array.from({ length: Math.min(5, activationsTotalPages) }, (_, i) => {
                          let pageNum: number;
                          if (activationsTotalPages <= 5) {
                            pageNum = i + 1;
                          } else if (activationsPage <= 3) {
                            pageNum = i + 1;
                          } else if (activationsPage >= activationsTotalPages - 2) {
                            pageNum = activationsTotalPages - 4 + i;
                          } else {
                            pageNum = activationsPage - 2 + i;
                          }
                          
                          return (
                            <button
                              key={pageNum}
                              onClick={() => handleActivationsPageChange(pageNum)}
                              className={`px-3 py-2 rounded-lg transition-colors ${
                                pageNum === activationsPage
                                  ? 'bg-white text-black'
                                  : 'text-white hover:bg-white/10'
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                      </div>
                      
                      <button
                        onClick={() => handleActivationsPageChange(activationsPage + 1)}
                        disabled={activationsPage >= activationsTotalPages}
                        className={`p-2 rounded-lg transition-colors ${
                          activationsPage >= activationsTotalPages
                            ? 'text-tesla-gray cursor-not-allowed'
                            : 'text-white hover:bg-white/10'
                        }`}
                      >
                        <ChevronRight className="w-5 h-5" />
                      </button>
                    </div>
                  )}
                </>
              )}
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PromoCodes;